import PrivateVenueContactEditor from "@/pages/discover/PrivateVenueContactEditor";
import PublicVenueFacts from "@/pages/discover/PublicVenueFacts";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { getBookingLeads, updateBookingLeadWindow } from "@/api/booking";
import { fetchCrmContact, fetchLeadDetail } from "@/api/crm";
import { fetchGraphVenues, type GraphVenue } from "@/api/discover";
import PageHeader from "@/components/PageHeader";
import { LeadDetailDrawer, type LeadDetailFormState } from "@/components/booking/LeadDetailDrawer";
import { KANBAN_COLUMNS, deriveLeadStage } from "@/components/booking/LeadsKanbanBoard";
import { CRMInsightsPanel } from "@/components/crm/CRMInsightsPanel";
import { CRMEpisodesList } from "@/components/memory/CRMEpisodesList";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { useSessionUser } from "@/hooks/useSessionUser";
import { mapBookingLeadToCrmLead, venueNameAndLocation } from "@/lib/booking";
import { useArtistScope } from "@/lib/artistContext";
import PitchSheet from "@/pages/discover/PitchSheet";
import VenueEvidenceSheet from "@/pages/discover/VenueEvidenceSheet";
import { formatDay, formatMonth, latestAppearanceByArtist } from "@/pages/discover/discoverCopy";
import { usePageBreadcrumbs } from "@/providers/BreadcrumbProvider";
import type { CrmContact, CrmLead } from "@/types/crm";

const SOURCE_LABELS: Record<string, string> = {
  discover_pitch: "Discover",
  "manual-booking": "Added manually",
};

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * Customer-facing booking detail, built only from data the app already stores:
 * the CRM lead, its contact, the matching Booking record and (for Discover leads)
 * the shared venue evidence. No enrichment, AI or external calls.
 */
export default function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const { selectedArtistId } = useArtistScope();
  const { user } = useSessionUser();
  const showInternal = Boolean(user?.is_owner); // platform owners only

  const [lead, setLead] = useState<CrmLead | null>(null);
  const [contact, setContact] = useState<CrmContact | null>(null);
  const [bookingLead, setBookingLead] = useState<CrmLead | null>(null);
  const [venue, setVenue] = useState<GraphVenue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pitchOpen, setPitchOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContact(null);
    setBookingLead(null);
    setVenue(null);
    fetchLeadDetail(leadId)
      .then(async (payload) => {
        if (cancelled) return;
        setLead(payload);
        const metadata = (payload.metadata ?? {}) as Record<string, unknown>;
        const globalVenueId = text(metadata["global_venue_id"]);
        // Supporting reads are best-effort: a missing piece simply isn't shown.
        const [contactResult, bookingResult, venuesResult] = await Promise.allSettled([
          payload.contact_id ? fetchCrmContact(payload.contact_id) : Promise.resolve(null),
          getBookingLeads({ limit: 200 }),
          globalVenueId && selectedArtistId ? fetchGraphVenues(selectedArtistId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (contactResult.status === "fulfilled") setContact(contactResult.value);
        if (bookingResult.status === "fulfilled") {
          const record = bookingResult.value.find((item) => Number((item.lead ?? {})["crm_lead_id"]) === payload.id);
          setBookingLead(record ? mapBookingLeadToCrmLead(record) : null);
        }
        if (venuesResult.status === "fulfilled" && venuesResult.value) {
          setVenue(venuesResult.value.venues.find((item) => item.global_venue_id === globalVenueId) ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setError("We couldn't load this booking. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selectedArtistId, reloadKey]);

  const { name, location } = venueNameAndLocation(lead ?? { title: null, region: null });
  const metadata = (lead?.metadata ?? {}) as Record<string, unknown>;
  const stage = lead ? KANBAN_COLUMNS.find((column) => column.id === deriveLeadStage(lead)) : null;
  const contactedAt = text(metadata["contacted_at"]);
  const globalVenueId = text(metadata["global_venue_id"]);
  const fromDiscover = lead?.source === "discover_pitch" || Boolean(globalVenueId);
  const sourceLabel = SOURCE_LABELS[String(lead?.source ?? "")] ?? null;
  const window = bookingLead?.event_window ?? lead?.event_window ?? null;
  const notes = text(lead?.description) ?? text(metadata["notes"]) ?? text((bookingLead?.metadata ?? {})["notes"]);
  const pitchable = Boolean(globalVenueId) && venue?.pitchable !== false;

  const latestByArtist = useMemo(() => latestAppearanceByArtist(venue), [venue]);

  const breadcrumbs = useMemo(
    () => [{ label: "Booking", href: "/booking" }, { label: lead ? name : `Booking ${leadId ?? ""}` }],
    [lead, name, leadId],
  );
  usePageBreadcrumbs(breadcrumbs);

  const handleSaveWindow = async (updates: LeadDetailFormState) => {
    if (!bookingLead) return;
    await updateBookingLeadWindow(bookingLead.id, updates.windowStart, updates.windowEnd);
    setReloadKey((key) => key + 1);
  };

  const contactLines = contact
    ? [
        text(contact.contact_name) && text(contact.contact_name) !== name ? text(contact.contact_name) : null,
        text(contact.email),
        text(contact.phone),
      ].filter(Boolean)
    : [];

  return (
    <div className="aa-page-shell space-y-4">
      <PageHeader
        title={lead ? name : "Booking"}
        description={[location, stage?.title].filter(Boolean).join(" · ") || undefined}
        actions={
          <div className="flex flex-wrap gap-2">
            {bookingLead && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                Edit booking
              </Button>
            )}
            {lead && pitchable && <Button onClick={() => setPitchOpen(true)}>Open pitch</Button>}
            <Button variant="ghost" asChild>
              <Link to="/booking">Back to Booking</Link>
            </Button>
          </div>
        }
      />
      {loading && !lead && <p className="text-sm text-muted-foreground">Loading booking…</p>}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {lead && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="aa-glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Booking</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm" aria-label="Booking details">
                <dt className="text-muted-foreground">Stage</dt>
                <dd className="text-foreground">{stage?.title ?? "Prospecting"}</dd>
                {contactedAt && (
                  <>
                    <dt className="text-muted-foreground">Contacted</dt>
                    <dd className="text-foreground">{formatDay(contactedAt)}</dd>
                  </>
                )}
                {window && (
                  <>
                    <dt className="text-muted-foreground">Booking window</dt>
                    <dd className="text-foreground">
                      {formatDay(window.start)}
                      {window.end && window.end !== window.start ? ` – ${formatDay(window.end)}` : ""}
                    </dd>
                  </>
                )}
                {sourceLabel && (
                  <>
                    <dt className="text-muted-foreground">Source</dt>
                    <dd className="text-foreground">{sourceLabel}</dd>
                  </>
                )}
                {notes && (
                  <>
                    <dt className="text-muted-foreground">Notes</dt>
                    <dd className="whitespace-pre-line text-foreground">{notes}</dd>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>

          {fromDiscover && (
            <Card className="aa-glass-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Why this venue</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">You found this venue in Discover.</p>
                {venue ? (
                  <>
                    {venue.identity?.status === "needs_review" && (
                      <p className="text-xs text-amber-600 dark:text-amber-300">
                        {venue.identity.reason ?? "This venue's identity needs review."}
                      </p>
                    )}
                    <p className="font-medium text-foreground">
                      Played by {venue.comparable_artist_count} of your comparable artist
                      {venue.comparable_artist_count === 1 ? "" : "s"}
                    </p>
                    <ul className="text-muted-foreground" aria-label={`Evidence for ${name}`}>
                      {latestByArtist.map((event) => (
                        <li key={event.public_artist_id}>
                          <span className="text-foreground/90">{event.artist_name}</span> · {formatMonth(event.event_date)}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="text-xs font-medium text-primary hover:underline"
                      onClick={() => setEvidenceOpen(true)}
                    >
                      See {venue.event_count} appearance{venue.event_count === 1 ? "" : "s"}
                    </button>
                  </>
                ) : (
                  !loading && (
                    <p className="text-xs text-muted-foreground">
                      Comparable-artist evidence isn't available for this venue right now.
                    </p>
                  )
                )}
              </CardContent>
            </Card>
          )}
          {(Boolean(lead.public_venue?.facts.length) || contactLines.length > 0 || Boolean(globalVenueId)) && (
          <Card className="aa-glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Venue details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {globalVenueId && selectedArtistId && <PrivateVenueContactEditor globalVenueId={globalVenueId} artistId={selectedArtistId} />}
              <h3 className="mt-4 font-semibold">Public venue information</h3>
              <PublicVenueFacts facts={lead?.public_venue?.facts ?? []} />
              {!globalVenueId && contactLines.length ? (
                <ul className="mt-4 space-y-0.5 text-foreground" aria-label="Your private contact details">
                  {contactLines.map((line) => (
                    <li key={line as string}>{line}</li>
                  ))}
                </ul>
              ) : (
                null
              )}
            </CardContent>
          </Card>
          )}

        </div>
      )}

      {showInternal && lead && (
        <details
          className="aa-glass-card rounded-2xl p-4 text-sm"
          onToggle={(event) => setInternalOpen(event.currentTarget.open)}
        >
          <summary className="cursor-pointer font-medium text-muted-foreground">Internal details (platform owners)</summary>
          {/* Mounted only when opened, so agent/memory panels make no requests by default. */}
          {internalOpen && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <CodeBlock value={JSON.stringify(lead.metadata ?? {}, null, 2)} language="json" />
              <CRMEpisodesList entityType="lead" entityId={leadId} limit={12} />
            </div>
            <CRMInsightsPanel objectType="lead" objectId={leadId} objectName={lead.title ?? undefined} />
          </div>
          )}
        </details>
      )}

      {/* Mounted only while editing: the drawer's legacy email lookup must not run on page load. */}
      {editOpen && bookingLead && (
        <LeadDetailDrawer
          open
          lead={bookingLead}
          canEdit
          mode="window"
          onClose={() => setEditOpen(false)}
          onSave={handleSaveWindow}
        />
      )}
      {evidenceOpen && <VenueEvidenceSheet venue={venue} onClose={() => setEvidenceOpen(false)} />}
      {selectedArtistId && (
        <PitchSheet
          artistId={selectedArtistId}
          globalVenueId={pitchOpen ? globalVenueId : null}
          venueName={name}
          onClose={() => setPitchOpen(false)}
          onStateChange={() => setReloadKey((key) => key + 1)}
        />
      )}
    </div>
  );
}
